import api, { getUserId } from './api';

/**
 * Выполняет аутентификацию пользователя через Telegram
 */
const authenticateUser = async () => {
  try {
    // Получаем данные пользователя из Telegram WebApp
    if (window.Telegram && window.Telegram.WebApp && window.Telegram.WebApp.initDataUnsafe) {
      const user = window.Telegram.WebApp.initDataUnsafe.user;
      
      if (user && user.id) {
        const userId = String(user.id);
        console.log(`[dashboardService] Выполняем аутентификацию для пользователя ${userId}`);
        
        const response = await fetch("https://fftg.farodinprojects.org:3000/auth", {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            "X-User-ID": userId,
            "X-Telegram-Auth": "true"
          },
          body: JSON.stringify({
            id: userId,
            first_name: user.first_name || "",
            last_name: user.last_name || "",
            username: user.username || "",
            photo_url: user.photo_url || "",
            language: user.language_code || 'ru',
            auth_source: "telegram_webapp"
          })
        });
        
        if (response.ok) {
          const data = await response.json();
          console.log('[dashboardService] Аутентификация успешна:', data);
          return true;
        } else {
          console.error('[dashboardService] Ошибка аутентификации:', await response.json());
          return false;
        }
      }
    }
    
    console.warn('[dashboardService] Данные пользователя Telegram недоступны для аутентификации');
    return false;
  } catch (error) {
    console.error('[dashboardService] Ошибка при аутентификации пользователя:', error);
    return false;
  }
};

/**
 * Получение данных для дашборда
 * @param {number} month - Номер месяца (1-12)
 * @param {boolean} includeShared - Включать ли данные от связанных аккаунтов
 * @returns {Promise<Object>} Данные дашборда
 */
export const fetchDashboardData = async (month, includeShared = true) => {
  try {
    // Всегда выполняем аутентификацию при загрузке данных дашборда
    console.log('[dashboardService] Выполняем аутентификацию пользователя...');
    const authResult = await authenticateUser();
    console.log('[dashboardService] Результат аутентификации:', authResult);

    // Используем безопасное получение ID пользователя из api.js
    const userId = getUserId();
    
    if (!userId) {
      console.error('[dashboardService] Нет ID пользователя для загрузки данных дашборда');
      return {
        balance: { amount: 0, currency: 'USD' },
        income: { amount: 0, currency: 'USD' },
        expenses: { amount: 0, currency: 'USD' },
        daysToSalary: 0,
        incomeComparison: null,
        expensesComparison: null
      };
    }

    console.log(`[dashboardService] Загрузка данных для месяца ${month}, ID: ${userId}, включая общие данные: ${includeShared}`);

    // Правильно передаем параметр shared
    const sharedParam = includeShared ? 'true' : 'false';
    console.log(`[dashboardService] Параметр shared: ${sharedParam}`);

    // Получение всех необходимых данных параллельно
    const requestPromises = [
      api.get(`/income`, { 
        params: { 
          user_id: userId, 
          month, 
          shared: sharedParam, // Явно передаем строку
          year: new Date().getFullYear()
        } 
      }),
      api.get(`/expenses`, { 
        params: { 
          user_id: userId, 
          month, 
          shared: sharedParam, // Явно передаем строку
          year: new Date().getFullYear()
        } 
      }),
      api.get(`/get_transferred_balance`, { 
        params: { 
          user_id: userId, 
          month, 
          shared: sharedParam, // Явно передаем строку
          year: new Date().getFullYear()
        } 
      }),
      api.get(`/get_salary_date`, { params: { user_id: userId } })
    ];

    console.log('[dashboardService] Отправляем запросы к API...');
    const [incomeResponse, expensesResponse, transferredBalanceResponse, salaryDateResponse] = await Promise.all(requestPromises);

    // Подробное логирование ответов для отладки
    console.log('[dashboardService] Детальные ответы API:');
    console.log('Income response:', {
      status: incomeResponse.status,
      data: incomeResponse.data,
      url: incomeResponse.config?.url,
      params: incomeResponse.config?.params
    });
    console.log('Expenses response:', {
      status: expensesResponse.status,
      data: expensesResponse.data,
      url: expensesResponse.config?.url,
      params: expensesResponse.config?.params
    });
    console.log('Transferred balance response:', {
      status: transferredBalanceResponse.status,
      data: transferredBalanceResponse.data,
      url: transferredBalanceResponse.config?.url,
      params: transferredBalanceResponse.config?.params
    });
    console.log('Salary date response:', {
      status: salaryDateResponse.status,
      data: salaryDateResponse.data
    });

    // Обработка данных для сравнения месяцев
    let incomeComparisonData = null;
    let expensesComparisonData = null;
    let incomeComparison = null;
    let expensesComparison = null;

    try {
  // Попытка получить данные предыдущего месяца для сравнения
  const prevMonth = month === 1 ? 12 : month - 1;
  const prevYear = month === 1 ? new Date().getFullYear() - 1 : new Date().getFullYear();

  console.log(`[dashboardService] Получение данных для сравнения: месяц ${prevMonth}, год ${prevYear}, shared: ${sharedParam}`);

  [incomeComparisonData, expensesComparisonData] = await Promise.all([
    api.get(`/income`, { 
      params: { 
        user_id: userId, 
        month: prevMonth, 
        year: prevYear,
        shared: sharedParam
      } 
    }),
    api.get(`/expenses`, { 
      params: { 
        user_id: userId, 
        month: prevMonth, 
        year: prevYear,
        shared: sharedParam
      } 
    })
  ]);

  console.log('[dashboardService] Ответы API для сравнения:', {
    incomeData: incomeComparisonData?.data,
    expensesData: expensesComparisonData?.data
  });

  // Функция для безопасного получения числовых значений из разных полей
  const extractValue = (data, fields) => {
    if (!data) return 0;
    
    for (const field of fields) {
      if (data[field] !== undefined && data[field] !== null) {
        const value = parseFloat(data[field]);
        if (!isNaN(value)) return value;
      }
    }
    return 0;
  };

  // Получаем текущие значения
  const currentIncome = extractValue(incomeResponse.data, ['total_income', 'total', 'amount']);
  const currentExpenses = extractValue(expensesResponse.data, ['total_expenses', 'total', 'amount']);
  
  // Получаем значения предыдущего месяца
  const prevIncome = extractValue(incomeComparisonData?.data, ['total_income', 'total', 'amount']);
  const prevExpenses = extractValue(expensesComparisonData?.data, ['total_expenses', 'total', 'amount']);

  console.log('[dashboardService] Данные для сравнения после извлечения:', {
    currentIncome,
    prevIncome,
    currentExpenses,
    prevExpenses
  });

  // Расчет процентов только если предыдущие значения не нулевые
  incomeComparison = prevIncome > 0 
    ? ((currentIncome - prevIncome) / prevIncome) * 100 
    : null;
    
  expensesComparison = prevExpenses > 0 
    ? ((currentExpenses - prevExpenses) / prevExpenses) * 100 
    : null;

  console.log('[dashboardService] Результаты сравнения:', {
    incomeComparison,
    expensesComparison
  });
} catch (error) {
  console.warn('[dashboardService] Ошибка при получении данных для сравнения:', error);
}

    // Извлечение и обработка основных данных с детальным логированием
    console.log('[dashboardService] Обработка основных данных...');
    
    // Проверяем все возможные поля для доходов
    let income = 0;
    if (incomeResponse.data) {
      console.log('[dashboardService] Структура данных доходов:', incomeResponse.data);
      
      if (typeof incomeResponse.data.total_income !== 'undefined') {
        income = parseFloat(incomeResponse.data.total_income) || 0;
        console.log('[dashboardService] Доходы из total_income:', income);
      } else if (typeof incomeResponse.data.total !== 'undefined') {
        income = parseFloat(incomeResponse.data.total) || 0;
        console.log('[dashboardService] Доходы из total:', income);
      } else if (typeof incomeResponse.data.amount !== 'undefined') {
        income = parseFloat(incomeResponse.data.amount) || 0;
        console.log('[dashboardService] Доходы из amount:', income);
      } else {
        console.warn('[dashboardService] Не найдено поле с доходами в ответе:', Object.keys(incomeResponse.data));
      }
    }
    
    // Проверяем все возможные поля для расходов
    let expenses = 0;
    if (expensesResponse.data) {
      console.log('[dashboardService] Структура данных расходов:', expensesResponse.data);
      
      if (typeof expensesResponse.data.total_expenses !== 'undefined') {
        expenses = parseFloat(expensesResponse.data.total_expenses) || 0;
        console.log('[dashboardService] Расходы из total_expenses:', expenses);
      } else if (typeof expensesResponse.data.total !== 'undefined') {
        expenses = parseFloat(expensesResponse.data.total) || 0;
        console.log('[dashboardService] Расходы из total:', expenses);
      } else if (typeof expensesResponse.data.amount !== 'undefined') {
        expenses = parseFloat(expensesResponse.data.amount) || 0;
        console.log('[dashboardService] Расходы из amount:', expenses);
      } else {
        console.warn('[dashboardService] Не найдено поле с расходами в ответе:', Object.keys(expensesResponse.data));
      }
    }
    
    // Перенесенный баланс
    const transferredBalance = transferredBalanceResponse.data ? 
      parseFloat(transferredBalanceResponse.data.transferred_balance || 0) : 0;
    console.log('[dashboardService] Перенесенный баланс:', transferredBalance);
    
    // Получаем валюту из ответа или используем USD по умолчанию
    const currency = 
      incomeResponse.data?.currency || 
      expensesResponse.data?.currency || 
      transferredBalanceResponse.data?.currency ||
      'USD';
    console.log('[dashboardService] Валюта:', currency);
    
    // Расчет баланса с учетом перенесенного остатка
    const balance = income - expenses + transferredBalance;
    console.log('[dashboardService] Рассчитанный баланс:', balance);

    // Расчет дней до зарплаты
    let daysToSalary = 0;
    if (salaryDateResponse.data && salaryDateResponse.data.salary_date) {
      const salaryDate = parseInt(salaryDateResponse.data.salary_date, 10);
      if (!isNaN(salaryDate) && salaryDate > 0) {
        const today = new Date();
        const currentDay = today.getDate();
        
        if (currentDay > salaryDate) {
          // Зарплата уже была в этом месяце, рассчитываем на следующий
          const nextMonth = new Date(today.getFullYear(), today.getMonth() + 1, salaryDate);
          daysToSalary = Math.ceil((nextMonth - today) / (1000 * 60 * 60 * 24));
        } else {
          // Зарплата еще не была в этом месяце
          const thisMonth = new Date(today.getFullYear(), today.getMonth(), salaryDate);
          daysToSalary = Math.ceil((thisMonth - today) / (1000 * 60 * 60 * 24));
        }
      }
    }
    console.log('[dashboardService] Дни до зарплаты:', daysToSalary);

    const result = {
      balance: { amount: Number(balance), currency },
      income: { amount: Number(income), currency },
      expenses: { amount: Number(expenses), currency },
      daysToSalary,
      incomeComparison,
      expensesComparison
    };

    console.log('[dashboardService] Финальный результат:', result);

    return result;
  } catch (error) {
    console.error('[dashboardService] Ошибка при загрузке данных дашборда:', error);
    console.error('[dashboardService] Stack trace:', error.stack);
    // В случае ошибки возвращаем нулевые данные
    return {
      balance: { amount: 0, currency: 'USD' },
      income: { amount: 0, currency: 'USD' },
      expenses: { amount: 0, currency: 'USD' },
      daysToSalary: 0,
      incomeComparison: null,
      expensesComparison: null
    };
  }
};